import { useEffect, useSyncExternalStore } from 'react'
import { reaction } from 'mobx'

import { useStore } from '@store/StoreProvider'
import type { BillingAccessStore } from '@store/BillingAccessStore'

function subscribeBilling(billing: BillingAccessStore, onChange: () => void): () => void {
  return reaction(
    () => [
      billing.entitlements,
      billing.loading,
      billing.error,
      billing.workspaceRestricted,
      billing.restrictionReason,
      billing.restrictionCode,
      billing.canUseWorkspace,
      billing.canManageBilling,
      billing.notice,
      billing.noticeHidden,
      billing.billingAlert,
      billing.expiredPromptOpen,
      billing.connectionEpoch,
      billing.accessCheckPending,
      billing.awaitingEmailVerification,
    ],
    onChange,
  )
}

export function useBillingEntitlements() {
  const { auth, i18n, billing, emailVerification } = useStore()
  const apiBaseUrl = (i18n.apiBaseUrl || '').replace(/\/$/, '')
  const emailUnverified = emailVerification.blocked || auth.currentUser?.emailVerified === false

  useSyncExternalStore(
    (onChange) => subscribeBilling(billing, onChange),
    () =>
      `${billing.connectionEpoch}|${billing.loading}|${billing.error?.code ?? ''}|${billing.error?.message ?? ''}|${billing.workspaceRestricted}|${billing.restrictionCode ?? ''}|${billing.notice?.code ?? ''}|${billing.noticeHidden ? '1' : '0'}|${billing.billingAlert}|${billing.entitlements?.generatedAt ?? ''}|${billing.awaitingEmailVerification ? '1' : '0'}`,
  )

  useEffect(() => {
    if (!auth.isAuthenticated || !apiBaseUrl) {
      billing.reset()
      return
    }
    if (emailUnverified) return
    void billing.ensureLoaded(apiBaseUrl, auth.authHeader)
  }, [auth.isAuthenticated, auth.accessToken, apiBaseUrl, billing, emailUnverified])

  return {
    entitlements: billing.entitlements,
    loading: billing.loading,
    error: billing.error,
    canUseWorkspace: billing.canUseWorkspace,
    canManageBilling: billing.canManageBilling,
    workspaceRestricted: billing.workspaceRestricted,
    restrictionReason: billing.restrictionReason,
    subscriptionAccessAllowed: billing.subscriptionAccessAllowed,
  }
}
